import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import { getCookie } from './utils/api';
import './index.css';

// --- Chunk-load error recovery ----------------------------------------------
// CRA code-splits every React.lazy() page into a hashed chunk file. When a new
// build is deployed, the browser may still have the OLD index.html cached,
// which references chunk hashes that no longer exist on the server →
// "Loading chunk X failed" / ChunkLoadError. Detect that and auto-reload once
// so the fresh index.html (with current chunk hashes) is fetched.
// Guard against infinite reload loops with a session flag.
const CHUNK_ERR_KEY = 'sms_chunk_reload_ts';
function isChunkError(payload) {
  const name = payload?.name || '';
  const msg = String(payload?.message || payload || '');
  return name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(msg) || /Loading CSS chunk .* failed/i.test(msg);
}
function handleChunkError(payload) {
  if (typeof window === 'undefined') return;
  const last = Number(sessionStorage.getItem(CHUNK_ERR_KEY) || 0);
  // Only auto-reload once per 10s window to avoid a reload loop.
  if (Date.now() - last < 10000) return;
  sessionStorage.setItem(CHUNK_ERR_KEY, String(Date.now()));
  // Bust the browser cache for index.html so we get the new chunk manifest.
  window.location.reload();
}
window.addEventListener('error', (e) => {
  if (isChunkError(e.error) || isChunkError(e.message)) handleChunkError(e.error || e.message);
});
window.addEventListener('unhandledrejection', (e) => {
  if (isChunkError(e.reason)) {
    e.preventDefault();
    handleChunkError(e.reason);
  }
});

// Auth uses an httpOnly cookie. Send it with every request — including the
// pages that still use the raw `axios` import rather than the shared `api`
// instance — so they authenticate via the cookie instead of a localStorage token.
axios.defaults.withCredentials = true;

// Raw-axios pages use relative paths like `/api/staff`. Without a baseURL those
// resolve to the frontend origin (e.g. http://localhost:3001), which serves the
// SPA index.html — not JSON — and crashes the page. Point raw axios at the
// backend origin so `/api/...` resolves correctly. The shared `api` client has
// its own baseURL (includes `/api`) and is unaffected.
axios.defaults.baseURL = process.env.REACT_APP_API_BASE_ORIGIN || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');

// Echo the CSRF double-submit token on state-changing raw-axios requests too.
const CSRF_SAFE = ['get', 'head', 'options'];
axios.interceptors.request.use((config) => {
  if (!CSRF_SAFE.includes((config.method || 'get').toLowerCase())) {
    const csrf = getCookie('csrfToken');
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
});

// Redirect to login on 401 from raw-axios pages (mirrors the shared `api` client).
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.startsWith('/auth')) {
      localStorage.removeItem('user');
      window.location.href = '/auth/login';
    }
    return Promise.reject(error);
  }
);

const theme = createTheme({
  palette: {
    primary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4f46e5',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ec4899',
      light: '#f472b6',
      dark: '#db2777',
      contrastText: '#ffffff',
    },
    success: {
      main: '#22c55e',
      light: '#86efac',
      dark: '#16a34a',
    },
    warning: {
      main: '#f59e0b',
      light: '#fcd34d',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444',
      light: '#fca5a5',
      dark: '#dc2626',
    },
    info: {
      main: '#06b6d4',
      light: '#67e8f9',
      dark: '#0891b2',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Nunito", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.025em' },
    h2: { fontWeight: 700, letterSpacing: '-0.025em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 600, letterSpacing: '-0.015em' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.025em' },
    body1: { fontWeight: 400, letterSpacing: '0.01em' },
    body2: { fontWeight: 400 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 24px',
          fontSize: '0.95rem',
        },
        contained: {
          boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.39)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
          '&.MuiPaper-root': {
            borderRadius: 12,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e2e8f0',
          fontSize: '0.875rem',
          padding: '12px 16px',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#f1f5f9',
          borderBottom: '2px solid #cbd5e1',
          color: '#334155',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(even)': {
            backgroundColor: '#f8fafc',
          },
          '&:hover': {
            backgroundColor: '#eef2ff !important',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.8125rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.07)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.875rem',
          textTransform: 'none',
          minHeight: 56,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
          borderBottom: '1px solid #e2e8f0',
          marginBottom: 0,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: '1px solid #e2e8f0',
          padding: '12px 24px',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#e2e8f0',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#cbd5e1',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#94a3b8',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#6366f1',
            borderWidth: 2,
          },
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <App />
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>
);